import { RedirectService } from '../../../login/redirect.service';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { of, pipe, timer } from 'rxjs';
import { HttpService, JWT_TYPE_NAME, ReefGatewayService, ReefHeaders, ReefKeepAliveService, ReefTokenService } from '@seco/core';
import {
  CORE_ACTION,
  DirectJwtGenerated,
  DirectLoggedIn,
  InitCustomerConfig,
  DirectLoggedInError,
  DirectLoggedOut,
  DirectRegistration,
  DirectAgreement,
  DirectSessionBanner,
  SetPOCustomerConfig,
  SetFMCustomerConfig,
  SetAVCInvoicesCustomerConfig,
  SetAVCDashboardsCustomerConfig,
  GetAVCCurrency,
  SetAVCCreditLineStatusCustomerConfig
} from '../../../login/core.actions';
import { LoginStatusResponse } from '../../../login/login.status.response';
import { select, Store } from '@ngrx/store';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { UserIdleService } from 'angular-user-idle';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ClpLoginResponse } from '@seco/login';
import { getDirectLogin, getDirectLoginClpResponse } from '../../../login/login-index.reducer';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { isOn } from 'feature-toggle-service';
import { ConfigService } from '../../../helpers/config.service';

const timeoutValue = 5000 * 60;
const headers = new HttpHeaders().set('Content-Type', 'application/json');
const httpOptions = { headers, withCredentials: true };

@Injectable()
export class SharedLoginEffects {
  private readonly LOGIN_API = 'amadeus-direct/validate-token';
  private readonly MAGENTO_LOGIN = 'amadeus/private/customer/login';
  private helper = new JwtHelperService();

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<any>,
    private readonly actions$: Actions,
    private readonly cookieService: CookieService,
    private readonly httpService: HttpService,
    private readonly userIdle: UserIdleService,
    private readonly redirectService: RedirectService,
    private readonly router: Router,
    private readonly initService: ConfigService,
    @Inject(DOCUMENT) private readonly doc,
    private readonly gwService: ReefGatewayService,
    private readonly reefKeepAliveService: ReefKeepAliveService,
    private readonly reeftokenservice: ReefTokenService
  ) {}

  checkSSO() {
    return (localStorage.getItem('source')?.toLowerCase() === 'ash' || localStorage.getItem('source')?.toLowerCase() === 'snow');
  }

  cleanOnLogoutWhenUserRoleHasBeenChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_USER_ROLE_HAS_BEEN_CHANGED),
      switchMap(() => {
        this.cookieService.set('unsignedAgreement', 'true');
        return of(new DirectLoggedOut({}));
      })
    );
  });

  cleanOnLogout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_USER_LOGGED_OUT),
      tap(() => {
        if (!this.checkSSO()) {
          this.userIdle.stopWatching();
          localStorage.removeItem('restoreToken');
          this.cookieService.delete('profile');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('magento_url');
          localStorage.removeItem('baseUrl');
          if (sessionStorage) {
            sessionStorage.clear();
          }
        }
      }),
      map(() => {
        if (!this.checkSSO()) {
           this.httpService.postApfPlus('amadeusdirect', 'logout').subscribe(() => {
            localStorage.removeItem('JsessionId');
          });
          this.gwService.logout().subscribe(() => {
            this.cookieService.delete('um_jst');
            this.cookieService.delete('JSESSIONID');
            this.cookieService.delete('JSESSIONID','/app_amadeus_direct');
          });
        }
      }),
      map(() => {
        return new DirectSessionBanner({ shownBanner: false, counter: 0 });
      }),
      // MMMW2-5810 TODO: refactor all services to be narrowly scoped and destroyed on navigation to login page
      // for now we just refresh the page on logout:
      tap(() => {
        if (!this.checkSSO()) {
          window.location.href = '/';
        } else {
          //if the Source is ASH, we do an auto-refresh of the page (till ASH session expires).
          window.location.reload();
        }
      })
    );
  });

  keepAliveUM$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CORE_ACTION.DIRECT_UPDATE_CLP_CONFIG),
        take(1),
        concatMap(() => timer(timeoutValue, timeoutValue)),
        switchMap(() => this.httpService.postDac('UMKeepAliveSession', {}))
      );
    },
    { dispatch: false }
  );

  keepAliveDirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CORE_ACTION.DIRECT_USER_LOGGED_IN),
        take(1),
        concatMap(() => timer(timeoutValue, timeoutValue)),
        switchMap(() => {
          this.reefKeepAliveService.start();
          return of(null);
        })
      );
    },
    { dispatch: false }
  );

  redirectOnLogin$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CORE_ACTION.DIRECT_USER_LOGGED_IN),
        tap(() => this.redirectService.loginRedirect())
      );
    },
    { dispatch: false }
  );

  redirectOnRegistration$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CORE_ACTION.DIRECT_REGISTRATION),
        tap(() => this.router.navigate(['/registration']))
      );
    },
    { dispatch: false }
  );

  redirectOnAgreement$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CORE_ACTION.DIRECT_AGREEMENT),
        tap(() => this.router.navigate(['/agreement']))
      );
    },
    { dispatch: false }
  );

requestHeaders(): {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | number | boolean;
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } {
    const options: any = { withCredentials: true };
    const jwtMap = this.reeftokenservice.getTokens();
    const jwt = jwtMap?.get(ReefHeaders.AUTH)?.get(JWT_TYPE_NAME);
      const headers = new HttpHeaders({
        Authorization: `${jwt}`
      });
      options.headers = headers;
    return options;
  }

  loginMagento = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_JWT_GENERATED),
      withLatestFrom(this.store.pipe(select(getDirectLogin))),
      map(([, directLogin]) => directLogin),
      pipe(
        switchMap((directLogin: any) =>
          this.http
            .get(this.initService.getEnvConfig().AD_MAGENTO_URL + this.MAGENTO_LOGIN, {
              headers: this.requestHeaders().headers
            })
            .pipe(  
              map((magentoLoginResponse: any) => {
                localStorage.setItem('restoreToken', directLogin?.directToken);
                localStorage.setItem('accessToken', directLogin?.clpResponse.accessToken);
                localStorage.setItem('idToken', directLogin?.clpResponse.idToken);
                localStorage.setItem('country_code', magentoLoginResponse.country_code);
                return new DirectLoggedIn({
                  profile: magentoLoginResponse
                });
              }),
              catchError(this.directLoginErrorAction.bind(this))
            )
        )
      ))});

  loadConfigOnLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_USER_LOGGED_IN),
      map(() => new InitCustomerConfig())
    );
  });

  sessionTimerStartEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_USER_LOGGED_IN),
      this.getClpResponse(),
      tap(() => this.userIdle.startWatching()),
      switchMap(() =>
        this.userIdle
          .onTimerStart()
          .pipe(map(count => new DirectSessionBanner({ shownBanner: count !== null, counter: +count })))
      )
    );
  });

  sessionOnTimeOutEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_USER_LOGGED_IN),
      this.getClpResponse(),
      switchMap(() => this.userIdle.onTimeout().pipe(map(() => new DirectLoggedOut())))
    );
  });

  loadFMPOConfigOnLogin$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CORE_ACTION.DIRECT_USER_LOGGED_IN),
        this.getClpResponse(),
        map((clpResponse: ClpLoginResponse) => {
          const decodedToken = this.helper.decodeToken(clpResponse.idToken);
          const currency = this.checkCurrency(decodedToken, 'AVC_TA_CURRENCY');
          if (this.checkRole(decodedToken, 'ACCESS_A_DIRECT_AS_ORDER')) {
            //set the product orderer role in the store
            this.store.dispatch(new SetPOCustomerConfig());
          }
          if (this.checkRole(decodedToken, 'ACCESS_A_DIRECT_AS_BILLING_MANAGER')) {
            //set the financial manager role in the store
            this.store.dispatch(new SetFMCustomerConfig());
          }
          if (this.checkRole(decodedToken, 'VIEW_AVC_DASHBOARDS')) {
            //set VIEW_AVC_DASHBOARDS role in the store
            this.store.dispatch(new SetAVCDashboardsCustomerConfig());
          }
          if (this.checkRole(decodedToken, 'VIEW_AVC_INVOICES')) {
            //set VIEW_AVC_INVOICES role in the store
            this.store.dispatch(new SetAVCInvoicesCustomerConfig());
          }
          if (currency) {
            //get AVC_TA_CURRENY value in the store
            this.store.dispatch(new GetAVCCurrency(currency));
          }
          if (this.checkRole(decodedToken, 'VIEW_AVC_CREDIT_LINE_STATUS')) {
            //set VIEW_AVC_DASHBOARDS role in the store
            this.store.dispatch(new SetAVCCreditLineStatusCustomerConfig());
          }
        })
      );
    },
    { dispatch: false }
  );

  sessionResumeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.DIRECT_RESUME_SESSION),
      tap(() => this.userIdle.resetTimer()),
      map(() => new DirectSessionBanner({ shownBanner: false }))
    );
  });

  private getClpResponse(): any {
    return pipe(
      withLatestFrom(this.store.pipe(select(getDirectLoginClpResponse))),
      map(([, clpResponse]) => clpResponse),
      filter(clpResponse => clpResponse && clpResponse.accessToken)
    );
  }

  private getOffice(decodedToken) {
    try {
      const officesConfig = decodedToken['acls']['RFD_OGA']['DISPLAY_LOCAL_OFFICE']['RFD']['OFF'];
      const organization = Object.keys(officesConfig)[0];
      return decodedToken['acls']['RFD_OGA']['DISPLAY_LOCAL_OFFICE']['RFD']['OFF'][organization][0];
    } catch (e) {
      return '';
    }
  }

  /**
   * Function to check if the roleString is present in the the decoded token and also checks the activation parameter
   * @param decodedToken is the decoded token containing the role and permissions
   * @param roleString is the string to be checked
   * @returns
   */
  private checkRole(decodedToken, roleString) {
    if (isOn('FMPOModuleAccessGuard')) {
      return (
        decodedToken['permissions_v2'] &&
        decodedToken['permissions_v2']['A_DIRECT'] &&
        decodedToken['permissions_v2']['A_DIRECT'].includes(roleString)
      );
    } else {
      return false;
    }
  }

  checkCurrency(decodedToken, currency) {
    if (isOn('AVC_TA_CURRENCY')) {
      if (
        decodedToken['preferences'] &&
        decodedToken['preferences']['MCONTRACT'] &&
        currency in decodedToken['preferences']['MCONTRACT']
      ) {
        return decodedToken['preferences']['MCONTRACT'][currency];
      }
    }
    return null;
  }

  private directLoginErrorAction(res: any) {
    if (res.status === LoginStatusResponse.REGISTRATION) {
      return of(new DirectRegistration({ code: res.status }));
    } else if (res.status === LoginStatusResponse.AGREEMENT && +res.error.code === 40) {
      return of(new DirectAgreement({ code: res.error.code, parameters: res.error.parameters }));
    } else {
      return of(new DirectLoggedInError({ code: res.error.code }));
    }
  }
}
